export const QUADRANT_KEYS = Object.freeze({
  TL: "TL",
  TR: "TR",
  BL: "BL",
  BR: "BR",
  SUB: "SUB",
});

export function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function ensureMinMax(min, max) {
  const a = clampPercent(min);
  const b = clampPercent(max);
  return a <= b ? { min: a, max: b } : { min: b, max: a };
}

/**
 * Quadrant model (split-lines):
 * - X cutoff creates left (<xCutoff) vs right (>=xCutoff)
 * - Y cutoff creates bottom (<yCutoff) vs top (>=yCutoff)
 *
 * Sub-quadrant is an axis-aligned rectangle inside BL (bottom-left).
 * It is EXCLUSIVE and evaluated first.
 */
export function sanitizeQuadrantConfig(input) {
  const xCutoff = clampPercent(input?.xCutoff ?? 50);
  const yCutoff = clampPercent(input?.yCutoff ?? 50);

  const xBounds = ensureMinMax(input?.subRect?.xmin ?? 0, input?.subRect?.xmax ?? 25);
  const yBounds = ensureMinMax(input?.subRect?.ymin ?? 0, input?.subRect?.ymax ?? 25);

  // Enforce the sub-rect stays strictly within BL boundaries.
  // BL is x < xCutoff and y < yCutoff, so cap max to just below the cutoff.
  const EPS = 0.0001;
  const cappedXMax = Math.max(0, Math.min(xBounds.max, Math.max(0, xCutoff - EPS)));
  const cappedYMax = Math.max(0, Math.min(yBounds.max, Math.max(0, yCutoff - EPS)));

  const subRect = {
    xmin: Math.min(xBounds.min, cappedXMax),
    xmax: Math.max(Math.min(xBounds.min, cappedXMax), cappedXMax),
    ymin: Math.min(yBounds.min, cappedYMax),
    ymax: Math.max(Math.min(yBounds.min, cappedYMax), cappedYMax),
  };

  return { xCutoff, yCutoff, subRect };
}

export function getQuadrantKey(point, cfg) {
  const x = Number(point?.x);
  const y = Number(point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  const { xCutoff, yCutoff, subRect } = sanitizeQuadrantConfig(cfg);

  const inBL = x < xCutoff && y < yCutoff;
  if (inBL) {
    const inSub =
      x >= subRect.xmin &&
      x <= subRect.xmax &&
      y >= subRect.ymin &&
      y <= subRect.ymax;
    if (inSub) return QUADRANT_KEYS.SUB;
    return QUADRANT_KEYS.BL;
  }

  const right = x >= xCutoff;
  const top = y >= yCutoff;

  if (right && top) return QUADRANT_KEYS.TR;
  if (!right && top) return QUADRANT_KEYS.TL;
  if (right && !top) return QUADRANT_KEYS.BR;
  return QUADRANT_KEYS.BL;
}

export function computeQuadrantCounts(points, cfg) {
  const counts = {
    [QUADRANT_KEYS.TL]: 0,
    [QUADRANT_KEYS.TR]: 0,
    [QUADRANT_KEYS.BL]: 0,
    [QUADRANT_KEYS.BR]: 0,
    [QUADRANT_KEYS.SUB]: 0,
  };

  (Array.isArray(points) ? points : []).forEach((p) => {
    const key = getQuadrantKey(p, cfg);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });

  const total = Object.values(counts).reduce((s, v) => s + (Number(v) || 0), 0);
  const percents = {};
  Object.keys(counts).forEach((k) => {
    percents[k] = total > 0 ? (counts[k] / total) * 100 : 0;
  });

  return { counts, percents, total };
}


